import { io, Socket } from 'socket.io-client'
import store from '@/store'
import { ChatMessage } from './ChatMessage'
import { ChatAttachment } from './ChatAttachment'

import { PersistentSendQueue } from './PersistentSendQueue'
import { Http } from '../utils/Http'
import { ProgressUploader } from '../utils/ProgressUploader'

export class ChatService {
  public static readonly CHAT_TYPE_RALLY_ROUND = 'CHAT_TYPE_RALLY_ROUND'

  private socket: Socket
  private chatType: string
  private chatIdentifier: number
  public sendQueue: PersistentSendQueue

  constructor (chatType: string, chatIdentifier: number) {
    this.socket = io(process.env.NODE_ENV === 'development' ? ':3001/chat' : '/chat', { path: '/socket.io', transports: ['websocket'] })
    this.chatType = chatType
    this.chatIdentifier = chatIdentifier
    this.sendQueue = new PersistentSendQueue(this)
  }

  get connected () {
    return this.socket.connected
  }

  public initiate () {
    this.socket.emit('get_last_messages', this.chatType, this.chatIdentifier)
    this.socket.io.on('reconnect', () => {
      console.log('reconnect happened!')
      this.sendQueue.upload()
    })
    this.socket.on('connect_error', () => {
      setTimeout(() => {
        this.socket.connect()
      }, 1000)
    })
    this.socket.on('last_messages', (messages) => {
      if (!messages.length || messages[0].rallyRoundId !== this.chatIdentifier) {
        // last messages empty or wrong rally round id, do nothing
        return
      }
      store.commit('updateWithOlderMessages', messages) // always use older messages method
      messages // remove all messages that are on the server and don't have attachments
        // .filter((message: ChatMessage) => !message.attachments.length)
        .forEach((message: ChatMessage) => {
          this.sendQueue.removeFromQueue(message)
        })
    })
    this.socket.on('older_messages', (messages) => {
      store.commit('updateWithOlderMessages', messages)
    })
    this.socket.on('received_message', (message: ChatMessage) => {
      /* if (message.uploaded) {
        this.sendQueue.removeFromQueue(message)
      } */

      // message saved, upload attachments (if none will skip)
      this.sendQueue.sendQueue.forEach(chatMessage => {
        let totalLength = 0
        chatMessage.attachments.forEach(attachment => {
          if (!attachment.rawFile) {
            return
          }
          totalLength += attachment.rawFile?.size
        })

        if (totalLength < 1024 * 1024 * 80) {
          this.uploadAttachmentsMultipart(chatMessage) // cloudflare has 100MB limit, smaller 100MB just upload multipart, seems more stable
        } else {
          this.uploadAttachmentChunks(chatMessage)
        }
        // this.uploadAttachmentWhole(chatMessage) DISABLED, UPLOAD IN ONE PART
      })
    })
    this.socket.on('upload_finished', (msg: ChatMessage, attachment: ChatAttachment) => {
      if (!attachment.uuid) {
        return
      }
      const att = this.sendQueue.findAttachmentByUUID(attachment.uuid)
      if (att) {
        att.uploaded = true
      }
      const message = this.sendQueue.findMessageByAttachmentUUID(attachment.uuid)
      if (!message) {
        return
      }

      // check if all attachments have been uploaded, remove from send queue if so
      if (!message?.attachments.filter(att => att.uploaded === false).length) {
        this.sendQueue.removeFromQueue(message)
      }
    })
    this.socket.on('got_uploaded_chunk', (uuid: string, chunkBytes: number) => {
      this.gotChunkUploaded(uuid, chunkBytes)
    })
  }

  private async uploadAttachmentsMultipart (chatMessage: ChatMessage) {
    for (let i = 0; i < chatMessage.attachments.length; i++) {
      const attachment = chatMessage.attachments[i]
      if (!attachment.rawFile) {
        return
      }

      if (!attachment.uploadStarted) {
        attachment.uploadStarted = true
        const res = await ProgressUploader.uploadOctetStream(
          `/api/chat/posts/${chatMessage.uuid}/attachments/${attachment.uuid}`,
          attachment.rawFile,
          (e) => { attachment.uploadedChunkBytes = e.loaded /* console.log('progress event: ', ((e.loaded / e.total) * 100) + '%') */ }
        )
        console.log('finished: ', res)
      }
    }

    /* await Promise.all(chatMessage.attachments.map(async attachment => {
      if (!attachment.rawFile) {
        return
      }

      const res = await ProgressUploader.uploadOctetStream(
        `/api/chat/posts/${chatMessage.uuid}/attachments/${attachment.uuid}`,
        attachment.rawFile,
        (e) => { attachment.uploadedChunkBytes = e.loaded /* console.log('progress event: ', ((e.loaded / e.total) * 100) + '%') *//* }
      )
      console.log('finished: ', res)
    })) */
  }

  public addMessageToQueue (chatMessage: ChatMessage) {
    this.sendQueue.addToQueue(chatMessage)
  }

  public sendMessage (chatMessage: ChatMessage) {
    if (!chatMessage.uploaded) {
      const messageWithoutAttachmentSource = { ...chatMessage } // clone message, attachments are needed below
      messageWithoutAttachmentSource.attachments = messageWithoutAttachmentSource.attachments.map(attachment => {
        attachment = { ...attachment }
        attachment.source = ''
        attachment.rawFile = null
        return attachment
      })
      this.socket.emit('post_message', this.chatType, this.chatIdentifier, messageWithoutAttachmentSource)
    }
  }

  public gotChunkUploaded (uuid: string, chunkBytes: number) {
    const attachment = this.sendQueue.findAttachmentByUUID(uuid)
    const message = this.sendQueue.findMessageByAttachmentUUID(uuid)

    if (!attachment) {
      console.error('Could not find attachment from uploaded chunk message received, this should never happen!')
      return
    }
    if (!message) {
      console.error('Could not find message from uploaded chunk message received, this should never happen!')
      return
    }

    attachment.uploadedChunkBytes = chunkBytes

    console.log('Chunk Bytes: ', chunkBytes)
    console.log('File Size:', attachment.rawFile?.size)

    // if fully uploaded, send notification we are finished, maybe add checksum?
    if (attachment && attachment.rawFile && attachment.rawFile.size <= chunkBytes) {
      attachment.uploaded = true
      this.socket.emit('finished_attachment_upload', attachment.uuid)
    }

    // continue uploading
    this.uploadAttachmentChunks(message)
  }

  public uploadAttachmentChunks (chatMessage: ChatMessage) {
    const chunkSize = 1024 * 1024 * 20 // 20MB
    const attachmentsToUpload = chatMessage.attachments.filter(attachment => !attachment.uploaded)
    if (!attachmentsToUpload || !attachmentsToUpload.length) {
      return
    }
    const attachment = attachmentsToUpload[0]
    if ((!attachment.source && !attachment.rawFile) || attachment.uploaded) {
      return
    }
    /* const chunkedAttachment = attachment.source
      .substring(attachment.uploadedChunkBytes) // remove already uploaded bytes
      .match(/.{1,100000}/g) // 100kb byte parts */
    console.log('Uploaded Chunk Bytes: ' + attachment.uploadedChunkBytes)
    const chunkedAttachment = attachment.rawFile?.slice(attachment.uploadedChunkBytes, attachment.uploadedChunkBytes + chunkSize)
    if (!chunkedAttachment) {
      return
    }

    console.log('Chunk size: ' + chunkedAttachment.size)
    // always just upload one chunk then wait for confirmation
    this.socket.emit('attachment_part', chatMessage.uuid, attachment.uuid, attachment.uploadedChunkBytes, chunkedAttachment)
  }

  public uploadAttachmentWhole (chatMessage: ChatMessage) {
    chatMessage.attachments.forEach(attachment => {
      if (!attachment.source && !attachment.rawFile) {
        return
      }

      this.socket.emit('attachment_whole', chatMessage.uuid, attachment.uuid, attachment.rawFile, (prop: any) => { console.log('emmited'); console.log(prop) })
    })
  }

  public getOlderMessages (messageTimestamp: string, challengeId = 0) {
    this.socket.emit('get_older_messages', this.chatType, this.chatIdentifier, messageTimestamp, challengeId)
  }

  public getLastMessages () {
    this.socket.emit('get_last_messages', this.chatType, this.chatIdentifier)
  }
}
